<template>
  <div>
    <div class="container-fluid">
      <div class="page-header">
        <Breadcrumbs main="Super Admin" title="Plans" />
      </div>

      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Plans <small class="text-muted">({{ plans.length }})</small></h5>
            <b-button size="sm" variant="primary" @click="openCreate">
              <feather type="plus" size="14" class="mr-1"></feather>Add plan
            </b-button>
          </div>
          <p class="text-danger mb-0 mt-2" v-if="actionError">{{ actionError }}</p>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="text-muted mt-3 mb-0">Loading plans...</p>
          </div>
          <div v-else-if="loadError" class="text-center py-5">
            <p class="text-danger mb-0">{{ loadError }}</p>
            <button class="btn btn-outline-primary btn-sm mt-3" @click="load">Retry</button>
          </div>
          <template v-else>
            <div class="table-responsive datatable-vue text-center">
              <b-table
                show-empty
                striped
                hover
                head-variant="light"
                bordered
                stacked="md"
                :items="plans"
                :fields="fields"
              >
                <template #empty><span>No plan yet. Click “Add plan”.</span></template>
                <template #cell(price)="field">{{ money(field.item) }}</template>
                <template #cell(maxStores)="field">
                  {{ field.item.maxStores === -1 ? "Unlimited" : field.item.maxStores }}
                </template>
                <template #cell(active)="field">
                  <span :class="field.item.active ? 'badge badge-success' : 'badge badge-secondary'">
                    {{ field.item.active ? "Active" : "Hidden" }}
                  </span>
                </template>
                <template #cell(actions)="field">
                  <div class="row-actions" style="justify-content:center">
                    <button type="button" class="icon-btn" title="Edit plan" @click="openEdit(field.item)">
                      <feather type="edit-2" size="16"></feather>
                    </button>
                    <button type="button" class="icon-btn icon-btn-danger" title="Delete plan" @click="confirmDelete(field.item)">
                      <feather type="trash-2" size="16"></feather>
                    </button>
                  </div>
                </template>
              </b-table>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Création / édition d'un plan -->
    <b-modal ref="planModal" :title="editing ? 'Edit plan' : 'Add plan'" @ok="submit" :ok-disabled="saving">
      <b-form @submit.prevent>
        <b-form-group label="Name" label-for="plan-name" class="mb-2">
          <b-form-input id="plan-name" v-model="form.name" placeholder="e.g. Business"></b-form-input>
        </b-form-group>
        <div class="form-row">
          <div class="col-6">
            <b-form-group label="Monthly price" label-for="plan-price" class="mb-2">
              <b-form-input id="plan-price" v-model="form.priceMonthly" type="number" step="0.01" min="0"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-6">
            <b-form-group label="Currency" label-for="plan-currency" class="mb-2">
              <b-form-select id="plan-currency" v-model="form.currency" :options="currencyOptions"></b-form-select>
            </b-form-group>
          </div>
        </div>
        <div class="form-row">
          <div class="col-6">
            <b-form-group label="Max stores (-1 = unlimited)" label-for="plan-stores" class="mb-2">
              <b-form-input id="plan-stores" v-model="form.maxStores" type="number" step="1" min="-1"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-6">
            <b-form-group label="Position" label-for="plan-position" class="mb-2">
              <b-form-input id="plan-position" v-model="form.position" type="number" step="1"></b-form-input>
            </b-form-group>
          </div>
        </div>
        <b-form-group label="Features (one per line)" label-for="plan-features" class="mb-2">
          <b-form-textarea id="plan-features" v-model="form.featuresText" rows="3" placeholder="Unlimited products&#10;Priority support"></b-form-textarea>
        </b-form-group>
        <b-form-group label="Stripe price ID (optional)" label-for="plan-stripe" class="mb-2">
          <b-form-input id="plan-stripe" v-model="form.stripePriceId"></b-form-input>
        </b-form-group>
        <b-form-group label="PayPal plan ID (optional)" label-for="plan-paypal" class="mb-2">
          <b-form-input id="plan-paypal" v-model="form.paypalPlanId"></b-form-input>
        </b-form-group>
        <b-form-checkbox v-model="form.active" class="mb-2">Active (visible to subscribers)</b-form-checkbox>
        <p class="text-danger mb-0" v-if="modalError">{{ modalError }}</p>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import firebase from "@/utils/firestore-shim";

export default {
  name: "AdminPlans",
  data() {
    return {
      fields: [
        { key: "name", label: "Name" },
        { key: "price", label: "Price" },
        { key: "maxStores", label: "Max stores" },
        { key: "position", label: "Position" },
        { key: "active", label: "Status" },
        { key: "actions", label: "" }
      ],
      currencyOptions: [
        { value: "usd", text: "USD ($)" },
        { value: "eur", text: "EUR (€)" }
      ],
      plans: [],
      loading: false,
      loadError: "",
      actionError: "",
      modalError: "",
      saving: false,
      editing: false,
      editingId: "",
      form: this.emptyForm()
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        priceMonthly: 0,
        currency: "usd",
        maxStores: 1,
        position: 0,
        featuresText: "",
        stripePriceId: "",
        paypalPlanId: "",
        active: true
      };
    },
    money(plan) {
      const symbol = String(plan.currency || "usd").toUpperCase() === "EUR" ? "€" : "$";
      return symbol + Number(plan.priceMonthly || 0).toFixed(2);
    },
    toast(message, type) {
      if (this.$toasted) {
        this.$toasted.show(message, { theme: "bubble", position: "bottom-right", type: type || "info", duration: 3000 });
      }
    },
    async load() {
      this.loading = true;
      this.loadError = "";
      this.actionError = "";
      try {
        const res = await firebase.api.get("/admin/plans");
        this.plans = (res && res.plans) || [];
      } catch (e) {
        this.loadError = "Could not load plans: " + ((e && e.message) || e);
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editing = false;
      this.editingId = "";
      this.modalError = "";
      this.form = this.emptyForm();
      this.$refs.planModal.show();
    },
    openEdit(plan) {
      this.editing = true;
      this.editingId = plan.id;
      this.modalError = "";
      this.form = {
        name: plan.name || "",
        priceMonthly: Number(plan.priceMonthly || 0),
        currency: plan.currency || "usd",
        maxStores: Number(plan.maxStores),
        position: Number(plan.position || 0),
        featuresText: (plan.features || []).join("\n"),
        stripePriceId: plan.stripePriceId || "",
        paypalPlanId: plan.paypalPlanId || "",
        active: Boolean(plan.active)
      };
      this.$refs.planModal.show();
    },
    payload() {
      const features = String(this.form.featuresText || "")
        .split("\n")
        .map((f) => f.trim())
        .filter((f) => f);
      return {
        name: String(this.form.name || "").trim(),
        priceMonthly: Number(this.form.priceMonthly) || 0,
        currency: this.form.currency || "usd",
        maxStores: Number(this.form.maxStores),
        position: Number(this.form.position) || 0,
        features: features,
        stripePriceId: this.form.stripePriceId || "",
        paypalPlanId: this.form.paypalPlanId || "",
        active: Boolean(this.form.active)
      };
    },
    async submit(event) {
      const body = this.payload();
      if (!body.name) {
        this.modalError = "Plan name is required.";
        event.preventDefault();
        return;
      }
      if (!Number.isFinite(body.maxStores)) {
        this.modalError = "Max stores must be a number (-1 for unlimited).";
        event.preventDefault();
        return;
      }
      this.saving = true;
      this.modalError = "";
      try {
        if (this.editing) {
          await firebase.api.patch("/admin/plans/" + this.editingId, body);
          this.toast("Plan updated.", "success");
        } else {
          await firebase.api.post("/admin/plans", body);
          this.toast("Plan created.", "success");
        }
        await this.load();
      } catch (e) {
        this.modalError = "Unable to save plan: " + ((e && e.message) || e);
        event.preventDefault();
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(plan) {
      this.$bvModal
        .msgBoxConfirm("Delete the plan " + plan.name + " ? Existing subscriptions keep their plan id.", {
          title: "Confirmation",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "DELETE",
          cancelTitle: "CANCEL",
          footerClass: "p-2"
        })
        .then((value) => {
          if (value) this.deletePlan(plan);
        })
        .catch(() => {});
    },
    async deletePlan(plan) {
      this.actionError = "";
      try {
        await firebase.api.request("DELETE", "/admin/plans/" + plan.id);
        this.toast("Plan deleted.", "success");
        await this.load();
      } catch (e) {
        this.actionError = "Unable to delete plan: " + ((e && e.message) || e);
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
